import React, { useState } from 'react';

import debounce from 'lodash-es/debounce';
import { useSelector } from 'react-redux';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import BitbucketBranchesIcon from '@atlaskit/icon/glyph/bitbucket/branches';
import Select, {
  OptionType,
  FormatOptionLabelMeta,
  ValueType,
} from '@atlaskit/select';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { useIntl } from 'src/hooks/intl';
import { FetchRefOptionsPayload } from 'src/redux/create-branch/actions';
import { BranchSelectorType } from 'src/redux/repository/branch/branch-redux';
import messages from 'src/sections/create-branch/components/create-branch.i18n';
import {
  CustomGroupHeading,
  CustomSelectOption,
} from 'src/sections/create-branch/components/custom-select-components';
import { LOADING_STATE } from 'src/sections/create-branch/constants';
import { Ref, RefOption } from 'src/sections/create-branch/types';
import { generateRefSelectorOptions } from 'src/sections/create-branch/utils';
import branchMessages from 'src/sections/repository/sections/pull-request-list/components/i18n';
import { getIsSiteMessageActive } from 'src/selectors/global-selectors';
import { getModalMenuPortalStyles } from 'src/styles/select';

type BranchSelectorProps = {
  branchSelector: BranchSelectorType;
  onChangeBranch: (branch: Ref | undefined) => void;
  fetchRefOptions: (payload?: FetchRefOptionsPayload) => void;
  fetchNextRefOptions?: (payload?: FetchRefOptionsPayload) => void;
  value?: ValueType<OptionType>;
  inputId?: string;
  autoFocus?: boolean;
  startMenuOpen?: boolean;
  isClearable?: boolean;
  placeholder?: string;
  styleOverride?: any;
  isDisabled?: boolean;
};

export const BranchSelector = (props: BranchSelectorProps): JSX.Element => {
  const intl = useIntl();
  const isBannerOpen = useSelector(getIsSiteMessageActive);
  const {
    inputId,
    fetchRefOptions,
    fetchNextRefOptions,
    branchSelector: { loadingState, refs, hasMoreRefs },
    onChangeBranch,
    value,
    placeholder,
    autoFocus = false,
    startMenuOpen = false,
    isClearable = false,
    styleOverride,
    isDisabled = false,
  } = props;

  const loadShed = FeatureGates.checkGate(StatsigFeatureKeys.loadShed);

  const handleSelectBranch = (option: RefOption | null) => {
    onChangeBranch(option?.ref);
  };

  const [hasInputChanged, setHasInputChanged] = useState(false);

  // @ts-ignore TODO: fix noImplicitAny error here
  const handleInputChange = (inputValue: string, { action }) => {
    if (action === 'input-change') {
      setHasInputChanged(true);
      fetchRefOptions({
        search: inputValue,
      });
      return;
    }

    if (action === 'menu-close' && hasInputChanged) {
      setHasInputChanged(false);
      fetchRefOptions();
    }
  };

  const debouncedInputChange = debounce(handleInputChange, 1000);

  const getOptions = () => {
    if (!refs.length) {
      return [];
    }

    const refOptions = generateRefSelectorOptions(refs);

    if (!hasMoreRefs) {
      return refOptions;
    }

    return [
      ...refOptions,
      {
        label: intl.formatMessage(messages.refSelectorMoreOptions),
        value: '',
        isDisabled: true,
        data: undefined,
      },
    ];
  };

  const formatOptionLabel = (
    option: OptionType,
    { context }: FormatOptionLabelMeta<OptionType>
  ): JSX.Element | string => {
    if (context === 'value') {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <BitbucketBranchesIcon size="medium" label="" />
          <span
            style={{
              paddingLeft: 8,
              paddingBottom: 0,
            }}
          >
            {option.label}
          </span>
        </div>
      );
    }
    return option && option.label;
  };

  let loadingPlaceholder: string | null = null;
  if (loadingState === LOADING_STATE.SUCCESS && !refs.length) {
    loadingPlaceholder = intl.formatMessage(
      branchMessages.filterBranchNoOptions
    );
  } else if (loadingState === LOADING_STATE.ERROR) {
    loadingPlaceholder = intl.formatMessage(branchMessages.filterBranchError);
  }

  const fetchMoreRefs = () => {
    if (!fetchNextRefOptions) return;

    fetchNextRefOptions();
  };

  const onMenuOpen = () => {
    if (loadShed && refs.length === 0) {
      fetchRefOptions();
    }
  };

  return (
    <Select
      isClearable={isClearable}
      backspaceRemovesValue={false}
      formatOptionLabel={formatOptionLabel}
      components={{
        GroupHeading: CustomGroupHeading,
        Option: CustomSelectOption,
      }}
      // We do our own filtering in handleInputChange, no need to filter again
      filterOption={null}
      isLoading={loadingState === LOADING_STATE.LOADING && !isDisabled}
      menuPortalTarget={document.body}
      noOptionsMessage={() =>
        intl.formatMessage(branchMessages.filterBranchNoOptions)
      }
      inputId={inputId}
      onChange={handleSelectBranch}
      onClickPreventDefault={false}
      onInputChange={debouncedInputChange}
      onMenuScrollToBottom={fetchMoreRefs}
      options={getOptions()}
      placeholder={placeholder || loadingPlaceholder}
      styles={styleOverride || getModalMenuPortalStyles(isBannerOpen)}
      value={value}
      defaultMenuIsOpen={startMenuOpen}
      autoFocus={autoFocus}
      isDisabled={isDisabled}
      onMenuOpen={onMenuOpen}
    />
  );
};
